import { useState, useEffect, lazy, Suspense } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import {
  useUser,
  useUserRoles,
  usePermissions,
} from "../../authentication/authentication";
import api from "../../api";
import errorChecker from "../../utils/errorChecker";
import { arClass } from "../../utils/useArClass";
import formatTime from "../../utils/formatTime";
import formatDate from "../../utils/formatDate";
import typeTranslator from "../../utils/useTypeTranslator";
import {
  CreditCard,
  TrendingUp,
  TrendingDown,
  Users,
  MessageSquareHeart,
  CalendarDays,
  User,
  Accessibility,
  Calendar,
  HeartPulse,
  Receipt,
  ScanHeart,
  SquareActivity,
  Microscope,
  DatabaseBackup,
  BadgeCheck,
} from "lucide-react";
import {
  FaUserMd,
  FaUsers,
  FaUserInjured,
  FaHdd,
  FaCalendarAlt,
  FaStethoscope,
  FaClipboardList,
  FaFileMedicalAlt,
  FaPlay,
  FaPause,
  FaChevronLeft,
  FaChevronRight,
  FaBook,
  FaVideo,
  FaHeadset,
  FaLightbulb,
  FaCalendarCheck,
  FaFileInvoice,
  FaFlask,
  FaPills,
} from "react-icons/fa";
import LoadingComponent from "../../components/common/LoadingComponent";
import { motion, AnimatePresence } from "framer-motion";
// Lazy-loaded components
const PatientStatistics = lazy(
  () => import("../../components/private/statistics/PatientStatistics"),
);
const RaqibCalendar = lazy(
  () => import("../../components/private/dashboard/RaqibCalendar"),
);

const Header = lazy(() => import("../../components/private/dashboard/Header"));
const UpcomingAppointments = lazy(() =>
  import("../../components/private/dashboard/UpcomingAppointments"),
);
const RecentPayments = lazy(() =>
  import("../../components/private/dashboard/RecentPayments"),
);
const UnauthorizedUserPermissions = lazy(
  () => import("../../components/UnauthorizedUserPermissions"),
);
const Txt = lazy(() => import("../../components/Text"));

import translationData from "../../pages/PRICING.json";
import { isAdminRole } from "../../utils/adminChecker";
import { checkAccess } from "../../utils/usePageAccess";

/**
 * Text component for the Dashboard page for translations
 * @param children - The text content to be translated
 * @param className - Optional CSS class names
 * @return Translated text element
 */
const Text = ({ children, className, page = "DASHBOARD" }) => {
  return (
    <Txt page={page} className={className} keyName={page}>
      {children}
    </Txt>
  );
};

/**
 * StatCard Component
 * @description A modern, refined statistic card with better visual hierarchy.
 * @param title - Title of the statistic
 * @param value - Value of the statistic
 * @param trend - Trend percentage
 * @param trendUp - Boolean indicating if trend is up
 * @param icon - Icon component to display
 * @param loading - Boolean indicating if data is loading
 * @param darkMode - Boolean indicating if dark mode is enabled
 * @return {JSX.Element} The StatCard component JSX element
 */
const StatCard = ({
  title,
  value,
  trend,
  trendUp,
  icon: Icon,
  loading,
  darkMode,
}) => {
  const trendColor = trendUp ? "text-emerald-600" : "text-rose-600";
  const trendBg = trendUp ? "bg-emerald-50" : "bg-rose-50";
  const trendBgDark = trendUp ? "bg-emerald-900/20" : "bg-rose-900/20";

  return (
    <motion.div
      className={arClass(
        `p-5 rounded-xl border transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 max-sm:p-4 ${
          darkMode
            ? "bg-slate-800/60 border-slate-700/50 hover:border-slate-600/80"
            : "bg-white/80 border-slate-200/70 hover:border-slate-300/80"
        }`,
      )}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className={arClass("flex justify-between items-start mb-3")}>
        <div
          className={arClass(
            `p-2.5 rounded-xl ${
              darkMode ? "bg-slate-700/50" : "bg-[#006096]/10"
            }`,
          )}
        >
          <Icon
            className={`w-5 h-5 ${darkMode ? "text-[#4a9bc7]" : "text-[#006096]"}`}
          />
        </div>
        {trend && trend !== "—" && (
          <div
            className={arClass(
              `flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                darkMode
                  ? `${trendBgDark} ${trendColor}`
                  : `${trendBg} ${trendColor}`
              }`,
            )}
          >
            {trendUp ? (
              <TrendingUp className="w-3 h-3" />
            ) : (
              <TrendingDown className="w-3 h-3" />
            )}
            {loading ? "—" : trend}
          </div>
        )}
      </div>
      <h3
        className={`text-xs font-medium uppercase tracking-wider max-sm:text-[10px] ${
          darkMode ? "text-slate-400" : "text-slate-500"
        }`}
      >
        <Text>{title}</Text>
      </h3>
      <p
        className={`text-2xl font-bold mt-1.5 max-sm:text-xl ${
          darkMode ? "text-white" : "text-slate-900"
        }`}
      >
        {loading ? "—" : value}
      </p>
    </motion.div>
  );
};

/**
 * RaqibDashboard Component
 * @description Main dashboard component displaying statistics and recent activities.
 * @return {JSX.Element} The RaqibDashboard component JSX element
 */
const RaqibDashboard = () => {
  const darkMode = useSelector((state) => state.settings.darkMode);
  const defaultLanguage =
    useSelector((state) => state.settings.defaultLanguage) || "en";
  // --- State Management ---
  const [counters, setCounters] = useState({
    totals: {
      totalPatients: 0,
      totalVisits: 0,
      totalPayments: 0,
      totalAppointments: 0,
    },
    metrics: {},
  });

  // New State for Lists
  const [recentAppointments, setRecentAppointments] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);

  // Loading States
  const [countersLoading, setCountersLoading] = useState(true);
  const [listsLoading, setListsLoading] = useState(true);

  // Get User Context
  const user = useUser();
  const roles = useUserRoles();
  const { permissions, loadingPermissions } = usePermissions();

  // --- Fetch Data ---
  useEffect(() => {
    let mounted = true;
    if (loadingPermissions) return;

    const fetchDashboardData = async () => {
      setCountersLoading(true);
      setListsLoading(true);

      try {
        // 1. Fetch Counters
        if (isAdminRole(roles)) {
          const counterParams = { periodDays: 30 };
          const counterRes = await api.get("/statistics/counters", {
            params: counterParams,
          });

          if (mounted && counterRes.data?.data) {
            const d = counterRes.data.data;
            setCounters({
              totals: d.totals || {
                totalPatients: 0,
                totalVisits: 0,
                totalPayments: 0,
                totalAppointments: 0,
              },
              metrics: d.metrics || {},
            });
          }
        }
      } catch (err) {
        // Handle error silently
      } finally {
        if (mounted) setCountersLoading(false);
      }

      // 2. Fetch Lists (Appointments & Payments) if user exists
      if (user?.id) {
        let promiseRequests = [];
        if (isAdminRole(roles)) {
          promiseRequests.push(
            api.get(`appointments/clinic-appointments?page=1&pageSize=12`),
          );
          promiseRequests.push(
            api.get(`payments/clinic-payments?page=1&pageSize=8`),
          );
        } else {
          if (checkAccess("getAppointment")) {
            promiseRequests.push(
              api.get(`appointments/user-id/${user.id}?page=1&pageSize=12`),
            );
          }
        }
        if (promiseRequests.length === 0) {
          if (mounted) setListsLoading(false);
          return;
        }
        try {
          if (isAdminRole(roles)) {
            const [appointmentsRes, paymentsRes] =
              await Promise.all(promiseRequests);

            if (mounted) {
              // Adjust '.data.data' based on your actual API response structure (e.g. data.content or just data)
              setRecentAppointments(appointmentsRes.data?.results || []);
              setRecentPayments(paymentsRes.data?.results || []);
            }
          } else {
            const results = await Promise.all(promiseRequests);

            if (mounted) {
              results.forEach((res) => {
                if (res.config.url.includes("appointments")) {
                  setRecentAppointments(res.data?.results || []);
                } else if (res.config.url.includes("payments")) {
                  setRecentPayments(res.data?.results || []);
                }
              });
            }
          }
        } catch (err) {
          // Handle error silently
        } finally {
          if (mounted) setListsLoading(false);
        }
      } else {
        if (mounted) setListsLoading(false);
      }
    };

    fetchDashboardData();

    return () => {
      mounted = false;
    };
  }, [user?.id, loadingPermissions]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.06,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 },
    },
  };

  return (
    <Suspense fallback={<LoadingComponent />}>
      <div className="flex font-sans text-slate-800 overflow-hidden">
        {/* --- Main Content --- */}
        <main
          className={arClass(
            "flex-1 flex flex-col h-full overflow-hidden relative p-4 space-y-6 max-sm:p-3",
          )}
        >
          <Header darkMode={darkMode} />

          {/* Scrollable Content */}
          <div
            className={arClass(
              "flex-1 space-y-6 overflow-y-auto pr-2 max-sm:space-y-4",
            )}
          >
            {/* 2. Stats Cards */}
            {isAdminRole(roles) && (
              <motion.div
                className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 max-sm:gap-3"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                <motion.div variants={itemVariants}>
                  <StatCard
                    title="Total Patients"
                    value={counters.totals.totalPatients ?? "—"}
                    trend={counters.metrics.patients?.changePercent ?? "—"}
                    trendUp={counters.metrics.patients?.direction === "up"}
                    icon={Users}
                    loading={countersLoading}
                    darkMode={darkMode}
                  />
                </motion.div>
                <motion.div variants={itemVariants}>
                  <StatCard
                    title="Today's Visits"
                    value={
                      counters.metrics.visits?.current ??
                      counters.totals.totalVisits ??
                      "—"
                    }
                    trend={counters.metrics.visits?.changePercent ?? "—"}
                    trendUp={counters.metrics.visits?.direction === "up"}
                    icon={MessageSquareHeart}
                    loading={countersLoading}
                    darkMode={darkMode}
                  />
                </motion.div>
                <motion.div variants={itemVariants}>
                  <StatCard
                    title="Appointments Today"
                    value={counters.totals.totalAppointments ?? "—"}
                    trend={counters.metrics.appointments?.changePercent ?? "—"}
                    trendUp={counters.metrics.appointments?.direction === "up"}
                    icon={CalendarDays}
                    loading={countersLoading}
                    darkMode={darkMode}
                  />
                </motion.div>
                <motion.div variants={itemVariants}>
                  <StatCard
                    title="Payments Today"
                    value={
                      counters.totals.totalPayments
                        ? `$${counters.totals.totalPayments}`
                        : "—"
                    }
                    trend={counters.metrics.payments?.changePercent ?? "—"}
                    trendUp={counters.metrics.payments?.direction === "up"}
                    icon={CreditCard}
                    loading={countersLoading}
                    darkMode={darkMode}
                  />
                </motion.div>
              </motion.div>
            )}

            {/* 3. Calendar Section */}
            <motion.div
              className="grid grid-cols-1 gap-6"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              {isAdminRole(roles) || checkAccess("getAppointment") ? (
                <RaqibCalendar />
              ) : (
                <UnauthorizedUserPermissions
                  permissionsKey="appointmentsPermissions"
                  method="get"
                />
              )}
            </motion.div>

            {/* 4. Charts Section */}
            <motion.div
              className="grid grid-cols-1 gap-6"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              {isAdminRole(roles) && <PatientStatistics />}
            </motion.div>

            {/* 5. Lower Section: Tables Grid */}
            <motion.div 
            className="grid grid-cols-1 lg:grid-cols-3 gap-6"
            variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <UpcomingAppointments
                darkMode={darkMode}
                roles={roles}
                recentAppointments={recentAppointments}
                listsLoading={listsLoading}
                permissions={permissions}
                defaultLanguage={defaultLanguage}
              />
              <RecentPayments
                darkMode={darkMode}
                roles={roles}
                recentPayments={recentPayments}
                listsLoading={listsLoading}
                permissions={permissions}
                defaultLanguage={defaultLanguage}
              />
            </motion.div>
            <div className="h-4"></div> {/* Bottom spacer */}
          </div>
        </main>
      </div>
    </Suspense>
  );
};

export default RaqibDashboard;